/**
 * useNimbusMath Hook
 * Hook para usar NimbusMath (MathPanel) en componentes React
 */

import { useRef, useCallback, useEffect } from 'react';
import { MathPanel } from './core';

export function useNimbusMath(options = {}) {
    const panelRef = useRef(null);
    const optionsRef = useRef(options);
    const editingElementRef = useRef(null); // Elemento que se está editando

    useEffect(() => {
        optionsRef.current = options;
        if (panelRef.current) {
            Object.assign(panelRef.current.options, options);
        }
    }, [options]);

    const getPanel = useCallback(() => {
        if (!panelRef.current) {
            panelRef.current = new MathPanel(optionsRef.current);
        }
        return panelRef.current;
    }, []);

    const open = useCallback((initialLatex = '', styleOptions = {}) => {
        editingElementRef.current = null; // No estamos editando
        getPanel().open(initialLatex, styleOptions);
    }, [getPanel]);

    const openWithMathML = useCallback((mathml) => {
        editingElementRef.current = null; // No estamos editando
        getPanel().openWithMathML(mathml);
    }, [getPanel]);
    
    // Nueva función para editar con elemento
    const editFormula = useCallback(({ latex, mathml, element, styles }) => {
        editingElementRef.current = element; // Guardar referencia al elemento
        // Preparar opciones de estilo si existen (usar nombres que _resetStyles espera)
        const styleOptions = styles ? {
            color: styles.color,
            size: styles.size,
            font: styles.font
        } : undefined;
        
        if (latex) {
            getPanel().open(latex, styleOptions);
        } else if (mathml) {
            getPanel().openWithMathML(mathml, styleOptions);
        } else {
            getPanel().open('', styleOptions);
        }
    }, [getPanel]);
    
    // Getter para el elemento en edición
    const getEditingElement = useCallback(() => {
        return editingElementRef.current;
    }, []);
    
    // Limpiar elemento en edición
    const clearEditingElement = useCallback(() => {
        editingElementRef.current = null;
    }, []);

    const close = useCallback(() => {
        panelRef.current?.close();
    }, []);

    const getValue = useCallback(() => {
        return panelRef.current?.getValue() || '';
    }, []);

    const getMathML = useCallback(() => {
        return panelRef.current?.getMathML() || '';
    }, []);

    const setValue = useCallback((latex) => {
        panelRef.current?.setValue(latex);
    }, []);

    const destroy = useCallback(() => {
        panelRef.current?.destroy();
        panelRef.current = null;
    }, []);

    // Métodos para estilos
    const setColor = useCallback((color) => {
        panelRef.current?.setColor(color);
    }, []);

    const setSize = useCallback((size) => {
        panelRef.current?.setSize(size);
    }, []);

    const setFont = useCallback((font) => {
        panelRef.current?.setFont(font);
    }, []);

    const getStyles = useCallback(() => {
        return panelRef.current?.getStyles() || { color: '', size: '', font: '' };
    }, []);

    useEffect(() => {
        return () => {
            panelRef.current?.destroy();
        };
    }, []);

    return {
        open,
        openWithMathML,
        editFormula,
        getEditingElement,
        clearEditingElement,
        close,
        getValue,
        getMathML,
        setValue,
        destroy,
        getPanel,
        // Estilos
        setColor,
        setSize,
        setFont,
        getStyles
    };
}

export default useNimbusMath;
